import { SidebarSection } from "@salestrak/ui";
import { useMemo } from "react";

import { EntityTag } from "#app/components/entity";
import { IssueMenuItems } from "#app/components/issue-actions/issue-menu";
import { useIssueView } from "#app/components/issue-view";
import {
  useRecentMapPlaces,
  type RecentMapPlace,
} from "#app/components/map/use-recent-map-places";
import type { IssueRow } from "#app/lib/data";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useLinkedIssueSummaries } from "#app/lib/hooks/use-linked-issue-summaries";
import { mapFocusSearch } from "#app/lib/map";
import { useCloseSidebarIfCompact } from "#app/lib/mobile-sidebar-context";
import { issueHref, mapPlaceHref } from "#app/lib/utils/entity-hrefs";
import { isAddressLike, streetLine } from "#app/lib/utils/format-address";

import { FavoriteItem } from "../favorite-item";

/**
 * One recent map place. The kind shows in the leading glyph the way the map
 * search's result rows tell the two apart — a pin for an address, the
 * project's type icon for a project — so a mixed list needs no group labels.
 * An address (or a project named by one) shows its street line and carries
 * the whole address in the tooltip, the favorites rows' abbreviation.
 *
 * Pressing a row goes back to the place through the map's one-shot focus
 * params — the door global search and the map's own search use — which pans
 * there and finds-or-creates the pin, popping its infobox. A project the map
 * never placed (no coordinates) opens in the project overlay instead, as its
 * search hit would.
 *
 * The ⋯ and right-click menus of a project row are the SAME roster a board
 * card, a table row and a favorites row open (`IssueMenuItems`), wired off
 * the LIVE row the section fetches for its project ids — so a recent offers
 * the project's actions in the same order and dress as everywhere else, and
 * its star is how a recent becomes a favorite. Until that row lands (or for
 * an address) the menu keeps the one action the row can serve on its own,
 * the place's link.
 */
function RecentMapPlaceRow({
  place,
  issue,
}: {
  place: RecentMapPlace;
  /** The live project row behind a project place, once fetched. */
  issue: IssueRow | undefined;
}) {
  const { activeWorkspace } = useWorkspace();
  const workspaceKey = activeWorkspace?.urlPath ?? "";
  const { push } = useAppNavigate();
  const { openIssueById } = useIssueView();
  const closeDrawer = useCloseSidebarIfCompact();

  const placed =
    typeof place.lat === "number" &&
    Number.isFinite(place.lat) &&
    typeof place.lng === "number" &&
    Number.isFinite(place.lng)
      ? { lat: place.lat, lng: place.lng, addressId: place.addressId }
      : null;
  const abbreviated = isAddressLike(place.name);

  const copyUrl =
    place.kind === "issue"
      ? place.workflowId
        ? issueHref(workspaceKey, place.workflowId, place.id)
        : undefined
      : placed
        ? mapPlaceHref(workspaceKey, placed)
        : undefined;

  return (
    <FavoriteItem
      actionLabel={place.kind === "issue" ? "Project actions" : "Place actions"}
      copyUrl={copyUrl}
      icon="map-pin"
      id={`${place.kind}-${place.id}`}
      isActive={false}
      items={
        issue
          ? (parts) => (
              <IssueMenuItems
                issue={issue}
                parts={parts}
                readOnly={!issue.viewerCanEdit}
              />
            )
          : undefined
      }
      name={abbreviated ? streetLine(place.name) : place.name}
      onClick={() => {
        closeDrawer();
        if (placed) {
          push(`/map?${mapFocusSearch(placed)}`);
          return;
        }
        if (place.kind === "issue") openIssueById(place.id, { source: "map_recents" });
      }}
      tooltip={abbreviated ? place.name : undefined}
      visual={
        place.kind === "issue" ? (
          <EntityTag
            display="visual"
            entity={{
              kind: "issue",
              id: place.id,
              issue: {
                name: place.name,
                icon: { icon: place.iconName, color: place.iconColor },
              },
              workflowId: place.workflowId ?? undefined,
            }}
            ghost={false}
            interactive={false}
          />
        ) : undefined
      }
    />
  );
}

/**
 * "Recent" module-menu section — the places the viewer went to on the map,
 * newest first, capped by `RECENT_ENTITY_LIMIT`. Renders nothing while empty,
 * and the menu drops it from its section list then so no divider strands.
 */
/**
 * The recent places the section will show, with the live row behind each
 * project place. One query for every project id: the menus read their gates
 * off the row, and a project that is gone (deleted, or no longer within the
 * viewer's reach) drops out rather than offering a dead row — a snapshot
 * stays on screen only until the server has answered. The Map menu reads the
 * same hook to decide whether the section exists at all, so the two never
 * disagree (Apollo dedupes the identical query).
 */
export function useVisibleRecentMapPlaces(): {
  places: RecentMapPlace[];
  issueById: Map<string, IssueRow>;
} {
  const { places } = useRecentMapPlaces();
  const projectIds = useMemo(
    () =>
      places
        .filter((place) => place.kind === "issue")
        .map((place) => place.id),
    [places],
  );
  const { issueById, initialLoading } = useLinkedIssueSummaries(projectIds);
  const visible = useMemo(
    () =>
      places.filter(
        (place) =>
          place.kind !== "issue" || initialLoading || issueById.has(place.id),
      ),
    [places, issueById, initialLoading],
  );
  return { places: visible, issueById };
}

export function MapRecentSection() {
  const { places, issueById } = useVisibleRecentMapPlaces();
  if (places.length === 0) return null;

  return (
    <SidebarSection title="Recent">
      {places.map((place) => (
        <RecentMapPlaceRow
          issue={place.kind === "issue" ? issueById.get(place.id) : undefined}
          key={`${place.kind}-${place.id}`}
          place={place}
        />
      ))}
    </SidebarSection>
  );
}
